import type { AnalysisDto } from "@eb/shared";
import { BookOpen, GraduationCap, Landmark } from "lucide-react";
import type { ReactNode } from "react";

interface AnalysisPanelProps {
    analysis: AnalysisDto | null;
    showEnglish: boolean;
    showChinese: boolean;
}

function paragraphs(text: string): string[] {
    return text
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph.length > 0);
}

function Section({
    icon,
    title,
    en,
    zh,
    showEnglish,
    showChinese
}: {
    icon: ReactNode;
    title: string;
    en: string;
    zh: string;
    showEnglish: boolean;
    showChinese: boolean;
}) {
    // Nothing to show if the learner turned off both languages for an EN/ZH-only section.
    if ((!showEnglish || !en) && (!showChinese || !zh)) return null;

    return (
        <section className="space-y-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
                <span className="text-brand">{icon}</span>
                {title}
            </h3>
            {/* Sections can run to several paragraphs (the distractor walk-through is
                its own paragraph); the parser joins them with blank lines. */}
            {showChinese && zh
                ? paragraphs(zh).map((text, index) => (
                      <p key={`zh-${index}`} className="text-[15px] leading-relaxed text-ink">
                          {text}
                      </p>
                  ))
                : null}
            {showEnglish && en
                ? paragraphs(en).map((text, index) => (
                      <p key={`en-${index}`} lang="en" className="text-sm leading-relaxed text-ink-soft">
                          {text}
                      </p>
                  ))
                : null}
        </section>
    );
}

/**
 * The study material behind a card: why the answer is right, the historical context, and
 * the B1-B2 vocabulary list. Which languages appear is the learner's setting -- someone
 * comfortable in German can switch both translations off and keep only the vocabulary.
 */
export function AnalysisPanel({ analysis, showEnglish, showChinese }: AnalysisPanelProps) {
    if (!analysis) {
        return <p className="text-sm text-ink-soft">这道题暂无解析。</p>;
    }

    return (
        <div className="space-y-6">
            <Section
                icon={<GraduationCap className="size-4" aria-hidden />}
                title="为什么选它 / Why this answer"
                en={analysis.reasonEn}
                zh={analysis.reasonZh}
                showEnglish={showEnglish}
                showChinese={showChinese}
            />
            <Section
                icon={<BookOpen className="size-4" aria-hidden />}
                title="主题与背景 / Topic"
                en={analysis.topicEn}
                zh={analysis.topicZh}
                showEnglish={showEnglish}
                showChinese={showChinese}
            />
            <Section
                icon={<Landmark className="size-4" aria-hidden />}
                title="历史社会背景 / Background"
                en={analysis.backgroundEn}
                zh={analysis.backgroundZh}
                showEnglish={showEnglish}
                showChinese={showChinese}
            />

            {analysis.vocab.length > 0 ? (
                <section className="space-y-2">
                    <h3 className="text-sm font-semibold text-ink">词汇 / Vokabeln</h3>
                    <div className="overflow-x-auto rounded-xl border border-line">
                        <table className="w-full min-w-[32rem] border-collapse text-sm">
                            <thead>
                                <tr className="bg-surface-sunken text-left text-xs text-ink-soft">
                                    <th className="px-3 py-2 font-medium">Deutsch</th>
                                    <th className="px-3 py-2 font-medium">English</th>
                                    <th className="px-3 py-2 font-medium">中文</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analysis.vocab.map((entry) => (
                                    <tr key={entry.german} className="border-t border-line">
                                        <td lang="de" className="px-3 py-2 font-medium text-ink">
                                            {entry.german}
                                        </td>
                                        <td lang="en" className="px-3 py-2 text-ink-soft">
                                            {entry.english}
                                        </td>
                                        <td className="px-3 py-2 text-ink-soft">{entry.chinese}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            ) : null}
        </div>
    );
}
